import { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { ApiError, downloadBlob, getBlob } from '../api/client';
import type { DocumentRow } from '../api/queries/documents';
import './DocumentPreviewModal.css';

/** Minimal fields the viewer needs — full DocumentRow or a synthetic receipt. */
export interface PreviewableFile {
  /** Auth-bearing API path, e.g. `/api/documents/12/download` or `/api/expenses/3/receipt`. */
  downloadUrl: string;
  filename: string;
  mimeType: string;
  /**
   * Bytes we already hold — a receipt picked in a form but not yet uploaded.
   * When set, nothing is fetched and `downloadUrl` is unused. Without this the
   * modal can only show files that already exist server-side, because it always
   * goes through `getBlob(downloadUrl)`.
   */
  blob?: Blob;
}

interface DocumentPreviewModalProps {
  /** Existing vault document (Documents / ProjectDocuments pages). */
  doc?: DocumentRow;
  /**
   * Generic preview source (expense receipts, etc.). Prefer this when the
   * download is not under `/api/documents/:id/download`.
   */
  file?: PreviewableFile;
  onClose: () => void;
}

/**
 * Which types we render inline. Deliberately limited to PDFs and images:
 * a blob: iframe of user-uploaded text/html would execute scripts in our
 * same-origin context (stored-XSS), so anything else falls back to Download.
 */
function inlineKind(mime: string): 'pdf' | 'image' | null {
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('image/')) return 'image';
  return null;
}

function resolveSource(
  doc: DocumentRow | undefined,
  file: PreviewableFile | undefined,
): PreviewableFile | null {
  if (file) return file;
  if (doc) {
    return {
      downloadUrl: `/api/documents/${doc.id}/download`,
      filename: doc.original_filename,
      mimeType: doc.mime_type,
    };
  }
  return null;
}

/**
 * Inline PDF/image preview modal. Used by Documents, ProjectDocuments, and
 * Expenses receipt column. Fetches with auth cookies via getBlob → object URL.
 */
export function DocumentPreviewModal({ doc, file, onClose }: DocumentPreviewModalProps): JSX.Element {
  const source = resolveSource(doc, file);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const kind = source ? inlineKind(source.mimeType) : null;
  const title = source?.filename ?? 'Preview';

  // Fetch the file (with auth) → object URL, mirroring UserAvatar's effect.
  // Only fetch for inline-renderable types; unpreviewable ones just show a
  // message and fetch on Download.
  useEffect(() => {
    if (!source || !kind) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setObjectUrl(null);
    let cancelled = false;
    let url: string | null = null;
    void (async () => {
      try {
        // A local blob short-circuits the fetch — same object-URL plumbing and
        // the same revoke on unmount, so the render path below is identical.
        const blob = source.blob ?? await getBlob(source.downloadUrl);
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setObjectUrl(url);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof ApiError && err.status === 404
            ? 'File missing from the vault.'
            : 'Could not load preview.',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
    // `blob` is in the deps because a local preview's downloadUrl is a
    // placeholder — without it, switching between two unsaved files would keep
    // showing the first.
  }, [source?.downloadUrl, source?.blob, kind]);

  const onDownload = () => {
    if (!source) return;
    const local = objectUrl ?? (source.blob ? URL.createObjectURL(source.blob) : null);
    if (local) {
      // Reuse the already-fetched blob — no second request.
      const a = document.createElement('a');
      a.href = local;
      a.download = source.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Only revoke what we just minted; the effect owns `objectUrl`.
      if (local !== objectUrl) setTimeout(() => URL.revokeObjectURL(local), 1000);
    } else {
      // Blob never loaded (unpreviewable type or load error) → fetch + save.
      void downloadBlob(source.downloadUrl, source.filename);
    }
  };

  const footer = (
    <button type="button" className="btn-cyan-primary" onClick={onDownload} disabled={!source}>
      Download
    </button>
  );

  return (
    <Modal open title={title} size="lg" bodyFlush onClose={onClose} footer={footer}>
      <div className="docprev-viewport">
        {!source ? (
          <p className="docprev-status docprev-error">Nothing to preview.</p>
        ) : loading ? (
          <p className="docprev-status">Loading preview…</p>
        ) : error ? (
          <p className="docprev-status docprev-error">{error}</p>
        ) : kind === 'pdf' && objectUrl ? (
          <iframe title={title} src={objectUrl} className="docprev-frame" />
        ) : kind === 'image' && objectUrl ? (
          <img src={objectUrl} alt={title} className="docprev-img" />
        ) : (
          <div className="docprev-status">
            <p>Preview isn’t available for this file type ({source.mimeType}).</p>
            <p className="docprev-hint">Use Download to open it on your device.</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
